import DOMPurify from 'dompurify';
import { getDocumentSourceImageUrl } from '../../get-document-source-image-url/index.js';
import { MediaTypeTranslation, getMediaType } from '../../media-type-translation/index.js';
import { version } from '../../version.js';

const [ MAJOR_VERSION ] = version.split('.');

export const ATTRIBUTES = {
    language: 'language'
};

export class PrDocumentCardHeader extends HTMLElement {
    #author;
    #country;
    #date;
    #document;
    #hideContentHeader = false;
    #mediaIcon;
    #mediaType;
    #source;

    static get tagName () {
        return `pr-document-card-header-v${MAJOR_VERSION}`;
    }

    get language() {
        return this.getAttribute(ATTRIBUTES.language);
    }

    set language (value) {
        this.setAttribute(ATTRIBUTES.language, value);
    }

    constructor () {
        super();
        this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback() {
        this.#render();
    }

    set document (value) {
        this.#country = value.country !== 'zz' ? value.country : '';
        this.#date = value.date;
        this.#document = value;
        this.#hideContentHeader = false;

        this.#mediaType = getMediaType(value);

        if (Array.isArray(value.authors)) {
            const [author] = value.authors;
            if (value.mediaType === 'twitter') {
                this.#author = author?.handle;
                this.#mediaIcon = 'x';
                this.#source = author?.name;
            } else if (this.#mediaType === 'reddit') {
                this.#author = author?.name;
                this.#mediaIcon = this.#mediaType;
                this.#source = value.source.replace(/^reddit.com\//, '');
            } else {
                this.#author = author?.name ? author?.name : author?.handle;
                this.#mediaIcon = this.#mediaType;
                this.#source = value.source;
            }
        } else if (value.source) {
            this.#author = '';
            this.#mediaIcon = this.#mediaType;
            this.#source = value.source;
        } else {
            this.#author = '';
            this.#hideContentHeader = true;
            this.#mediaIcon = '';
            this.#source = '';
        }

        this.#render();
    }

    #render () {
        this.shadowRoot.innerHTML = /*html*/`
            <style>
                :host {
                    container-type: inline-size;
                    display: flex;
                    flex: 1;
                    gap: 8px;
                }

                @container (width < 200px) {
                    flux-avatar {
                        display: none;
                    }
                }

                .metadata {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    font-size: 12px;
                    font-weight: 400;
                    justify-content: center;
                    line-height: 14px;
                }

                .content-header {
                    color: var(--flux-color-gray-800);
                    font-weight: 500;
                    white-space: nowrap;
                }

                .metadata.no-header {
                    justify-content: flex-end;
                }

                .metadata.no-header > .content-header {
                    display: none;
                }

                .content-footer {
                    color: var(--flux-color-gray-700);
                    display: flex;
                    gap: 4px;
                    white-space: nowrap;
                }

                .source-avatar {
                    height: 36px;
                    width: 36px;
                    flex-shrink: 0;
                }

                .media-type {
                    color: var(--flux-color-aqua);
                    font-weight: 700;
                }

                .country {
                    text-transform: uppercase;
                }

                flux-date-time {
                    display: inline;
                }
            </style>

            <flux-avatar rounded
                class="source-avatar"
                image-url="${getDocumentSourceImageUrl(this.#document)}"
                media-icon="${this.#mediaIcon}"
                name="${this.#document.source}"
                ></flux-avatar>
            <div class="metadata ${this.#hideContentHeader ? 'no-header' : ''}">
                <div class="content-header">
                    <span id="source">${DOMPurify.sanitize(this.#source)}</span>
                    <span id="separator">•</span>
                    <span id="author">${DOMPurify.sanitize(this.#author)}</span>
                </div>
                <div class="content-footer">
                    <span class="media-type"><${MediaTypeTranslation.tagName} language="${this.language}" type="${this.#mediaType}"></${MediaTypeTranslation.tagName}></span> |
                    <span class="country">${DOMPurify.sanitize(this.#country)} |</span>
                    <flux-date-time date="${this.#date}"></flux-date-time>
                </div>
            </div>
        `;

        if (!this.#author) {
            this.shadowRoot.querySelector('#author').style.display = 'none';
            this.shadowRoot.querySelector('#separator').style.display = 'none';
        }

        if (!this.#country || this.#country === 'zz') {
            this.shadowRoot.querySelector('.country').style.display = 'none';
        }

        if (!this.#date) {
            this.shadowRoot.querySelector('flux-date-time').style.display = 'none';
        }

        if (!this.#mediaType) {
            this.shadowRoot.querySelector('.media-type').style.display = 'none';
        }

        if (!this.#source) {
            this.shadowRoot.querySelector('#source').style.display = 'none';
            this.shadowRoot.querySelector('#separator').style.display = 'none';
        }

    }
}

if (!customElements.get(PrDocumentCardHeader.tagName)) {
    customElements.define(PrDocumentCardHeader.tagName, PrDocumentCardHeader);
}
